import { publishMQTTMessage } from './sendToRabbitMQ';
import { UAParser } from 'ua-parser-js';
import uuidV4 from 'uuid/v4';

import packageJson from '../../package.json';
import { API_ENDPOINTS, CANVAS_CONFIG, CHANNEL, DATA_LAYER_EVENTS, DEFAULT_LABELS, DEFAULT_LANGUAGE, DESCRIPTION_TEMPLATES, DEVICE_CONFIG, DTDL_HEADER_LINKS, ENVIRONMENTS, EVENT_ACTIONS, EVENT_CATEGORIES, EVENT_TYPE, NETWORK_CONFIG, STORAGE_KEYS, USER_TYPES } from './constant';

const appVersion = `${packageJson.name}@${packageJson.version}`;
const instanceId = process.env.REACT_APP_INSTANCE_ID || '';

const parser = new UAParser();
const result = parser.getResult();

/**
 * Dynamic identifiers builder
 * Environment, language, and user_type are no longer hardcoded
 */

export function getEnvironment(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '[::1]' || /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(hostname)) {
    return ENVIRONMENTS.LOCAL;
  }

  if (hostname.includes('staging') || hostname.includes('stage')) {
    return ENVIRONMENTS.STAGING;
  }

  if (hostname.includes('qa')) {
    return ENVIRONMENTS.QA;
  }

  if (hostname.includes('dev')) {
    return ENVIRONMENTS.DEVELOPMENT;
  }

  return ENVIRONMENTS.PRODUCTION;
}

function buildIdentifiers({
  deviceId,
  resolvedSessionId,
  userId,
  userType,
  environment,
  appLanguage,
}: {
  deviceId: string;
  resolvedSessionId: string;
  userId: string;
  environment?: string;
  appLanguage?: string;
  userType?: string;
}) {
  return {
    device_id: deviceId,
    install_id: '',
    session_id: resolvedSessionId,
    tenant_code: instanceId,
    environment: environment || getEnvironment(),
    app_version: appVersion,
    app_language: appLanguage || navigator.language || document.documentElement.lang || DEFAULT_LANGUAGE,
    user_id: userId,
    user_type: userType || localStorage.getItem(STORAGE_KEYS.USER_TYPE) || USER_TYPES.GUEST,
    build_number: '',
    bundle_id: '',
    ecid: '',
  };
}

// Session ID
function getSessionId(): { sessionId: string; isNewUser: boolean } {
  let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  let isNewUser = false;

  if (!sessionId) {
    sessionId = uuidV4();
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    isNewUser = true;
  }

  return { sessionId, isNewUser };
}


function getUserIdentifiers() {
  let _unusedUserId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  if (!_unusedUserId) {
    _unusedUserId = uuidV4();
    localStorage.setItem(STORAGE_KEYS.USER_ID, _unusedUserId);
  }

  let deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidV4();
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId);
  }

  return { userId: _unusedUserId, deviceId };
}

// Session info
function getSessionInfo(isNewUser: boolean) {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    visit_type: isNewUser ? 'new' : 'returning',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_term: urlParams.get('utm_term') || '',
  };
}

// Network info
function getNetworkInfo() {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;
    return {
      connection_type: connection ? connection.effectiveType || NETWORK_CONFIG.UNKNOWN : NETWORK_CONFIG.UNKNOWN,
      device_carrier: NETWORK_CONFIG.EMPTY,
      msisdn_carrier: NETWORK_CONFIG.EMPTY,
    };
}

// Page info
function getPageInfo() {
  const pageTitle = document.title || window.location.pathname;
  const currentUrl = window.location.href;
  const pageReferrer = localStorage.getItem(STORAGE_KEYS.LAST_PAGE) || '';
  localStorage.setItem(STORAGE_KEYS.LAST_PAGE, pageTitle);

  return { pageTitle, pageReferrer, currentUrl };
}

// Geolocation cache - stores data for session duration
let geoLocationCache: { city: string; country_name: string } | null = null;
let geoLocationFetchPromise: Promise<{ city: string; country_name: string }> | null = null;

// IP-based geolocation using ipwho.is (free, unlimited, commercial use allowed)
// Cached for session duration to improve performance (reduces 200-500ms to 0ms)
async function getGeoLocation(): Promise<{ city: string; country_name: string }> {
  // Return cached data if available
  if (geoLocationCache) {
    return geoLocationCache;
  }

  // If a fetch is already in progress, wait for it instead of making another request
  if (geoLocationFetchPromise) {
    return geoLocationFetchPromise;
  }

  // Fetch geolocation data
  geoLocationFetchPromise = (async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GEO_LOCATION);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const geo = await response.json();
      
      // Handle ipwho.is response structure
      // Note: Browser Geolocation API only provides coordinates, not city/country names
      // Would need additional reverse geocoding API to convert coordinates to location names
      const geoData = {
        city: geo.city || '',
        country_name: geo.country || '',
      };

      // Cache the result for session duration
      geoLocationCache = geoData;
      return geoData;
    } catch (error) {
      console.error('Error fetching IP geolocation:', error);
      // Return empty data but don't cache errors (will retry on next call)
      return { city: '', country_name: '' };
    } finally {
      // Clear the promise so we can retry if needed
      geoLocationFetchPromise = null;
    }
  })();

  return geoLocationFetchPromise;
}

async function getDeviceInfo() {
  const geo = await getGeoLocation();

  return {
    location_enabled: 'False', 
    user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    geo_city: geo.city || '',
    geo_country: geo.country_name || '',
    device_resolution: `${window.screen.width}x${window.screen.height}`,
    device_category: window.innerWidth <= DEVICE_CONFIG.MOBILE_BREAKPOINT ? DEVICE_CONFIG.CATEGORIES.MOBILE : DEVICE_CONFIG.CATEGORIES.DESKTOP,
    device_manufacturer: result.device.vendor || '',
    device_brand: result.device.vendor || '',
    device_model: result.device.model || '',
    os_name: navigator.platform || '',
    os_version:
      navigator.userAgent.match(/Mac OS X (\d+[_\d]+)/)?.[1].replace(/_/g, '.') ||
      '',
  };
}

/**
 * CLICK EVENT
 */
export async function sendClickEvent({
  eventLabel,
  attribute,
  orderId,
  blockName,
  sessionId,
  m_canvas,
}: {
  eventLabel: string;
  attribute: { description: string; channel?: string; pagename?: string };
  orderId?: number;
  blockName?: string;
  sessionId?: string;
  m_canvas?: {source: string;
    type: "Page" | "Component";
    id: string;
    component?: string;
    name: string;
    action: string;
    label: string;
    position?: string;};
}) {
  const sessionData = sessionId ? { sessionId, isNewUser: false } : getSessionId();
  const { sessionId: resolvedSessionId, isNewUser } = sessionData;
  const { userId, deviceId } = getUserIdentifiers();
  const device = await getDeviceInfo();
  const network = getNetworkInfo();
  const session = getSessionInfo(isNewUser);
  const { pageTitle, pageReferrer, currentUrl } = getPageInfo();

  let componentLabel: string = eventLabel;
let eventCategory: string = `${blockName || DEFAULT_LABELS.UNKNOWN}-${orderId ?? ''}-${currentUrl}`;
let componentPosition: string = `${orderId ?? ''}`;
let componentId: string = `${blockName || DEFAULT_LABELS.UNKNOWN}-${orderId ?? ''}`;

if (eventLabel in DTDL_HEADER_LINKS) {
  const headerLink = DTDL_HEADER_LINKS[eventLabel as keyof typeof DTDL_HEADER_LINKS];
  componentLabel = headerLink.label;
  eventCategory = headerLink.category;
  componentPosition = headerLink.position;
  componentId = headerLink.id;
}


  const event = {
    date: new Date().toISOString().split('T')[0],
    event_type: EVENT_TYPE,
    event_category: eventCategory,
    event_action: EVENT_ACTIONS.BUTTON_CLICKED,
    event_label: componentLabel,
    timestamp: {
      event_logtime: Date.now(),
      event_logtimestamp: new Date().toISOString(),
      cdl_time: '',
      cdl_timestamp: '',
      de_ingestion_id: '',
    },
    identifiers: buildIdentifiers({ deviceId, resolvedSessionId, userId, userType: localStorage.getItem(STORAGE_KEYS.USER_TYPE) || USER_TYPES.GUEST, environment: getEnvironment(), appLanguage: navigator.language || document.documentElement.lang || DEFAULT_LANGUAGE }),
    device,
    network,
    session,
    page: {
      page_name: pageTitle,
      previous_page_name: pageReferrer,
      url: currentUrl,
    },
    attribute: {
      ...attribute,
      channel: attribute.channel || CHANNEL.WEB,
      pagename: attribute.pagename || pageTitle,
    },
    custom_data: {
      order_id: orderId ?? undefined,
      blockName: blockName ?? undefined,
    },
    m_canvas: {
      ...m_canvas,
      source: CANVAS_CONFIG.SOURCE,
      type: CANVAS_CONFIG.TYPES.COMPONENT,
      id: componentId,
      component: blockName ?? undefined,
      name: String(blockName ?? DEFAULT_LABELS.UNKNOWN),
      action: CANVAS_CONFIG.ACTIONS.BUTTON_CLICKED,
      label: componentLabel,
      position: componentPosition,
    }
  };

  // Non-blocking publish - no await, no try-catch needed
  // Topic is determined automatically based on instance (dtdl.website or payzy.website)
  publishMQTTMessage(event);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: DATA_LAYER_EVENTS.BUTTON_CLICK, ...event } as ClickEventData);
}



/**
 * SCROLL DEPTH EVENT
 */
export async function sendScrollEvent({
  scrollDepthPercent,
  pageId,
  sessionId,
}: {
  scrollDepthPercent: number;
  pageId?: string;
  sessionId?: string;
}) {
  const sessionData = sessionId ? { sessionId, isNewUser: false } : getSessionId();
  const { sessionId: resolvedSessionId, isNewUser } = sessionData;
  const { userId, deviceId } = getUserIdentifiers();
  const device = await getDeviceInfo();
  const network = getNetworkInfo();
  const session = getSessionInfo(isNewUser);
  const { pageTitle, pageReferrer, currentUrl } = getPageInfo();

  const event = {
    date: new Date().toISOString().split('T')[0],
    event_type: EVENT_TYPE,
    event_category: EVENT_CATEGORIES.USER_ENGAGEMENT,
    event_action: EVENT_ACTIONS.SCROLL_DEPTH,
    event_label: `${scrollDepthPercent}%`,
    timestamp: {
      event_logtime: Date.now(),
      event_logtimestamp: new Date().toISOString(),
      cdl_time: String(Date.now()),
      cdl_timestamp: new Date().toISOString(),
      de_ingestion_id: '',
    },
    identifiers: buildIdentifiers({ deviceId, resolvedSessionId, userId, userType: localStorage.getItem(STORAGE_KEYS.USER_TYPE) || USER_TYPES.GUEST, environment: getEnvironment(), appLanguage: navigator.language || document.documentElement.lang || DEFAULT_LANGUAGE }),
    device,
    network,
    session,
    page: {
      page_name: pageTitle,
      previous_page_name: pageReferrer,
      url: currentUrl,
    },
    attribute: {
      description: DESCRIPTION_TEMPLATES.scrollDepth(scrollDepthPercent),
      channel: CHANNEL.WEB,
      pagename: pageTitle,
    },
    custom_data: {
      scroll_depth_percent: scrollDepthPercent,
    },
    m_canvas: {
      source: CANVAS_CONFIG.SOURCE,
      type: CANVAS_CONFIG.TYPES.PAGE,
      id: pageId || currentUrl,
      name: currentUrl,
      action: CANVAS_CONFIG.ACTIONS.PAGE_SCROLLED,
      label: `${scrollDepthPercent}%`,
      position: `${scrollDepthPercent}`,
    }
  };

  // Non-blocking publish - no await, no try-catch needed
  // Topic is determined automatically based on instance (dtdl.website or payzy.website)
  publishMQTTMessage(event);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 
    event: DATA_LAYER_EVENTS.SCROLL_DEPTH,
    scroll_depth_percent: scrollDepthPercent,
    page_url: currentUrl,
    ...event 
  });
}

export {
  getSessionId,
  getUserIdentifiers,
  getNetworkInfo,
  getDeviceInfo,
  getSessionInfo,
  getPageInfo,
  buildIdentifiers
};